"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Button, Modal } from "@/components/ui";

// Sign the customer out after a period of inactivity. The JWT itself lives for hours, so without
// this a session left open on a shared machine stays usable — a real risk for an account that can
// spend credit and read every message the business has sent.
const IDLE_MS = 30 * 60 * 1000; // 30 minutes of no interaction
const WARN_MS = 60 * 1000;      // ...with a 60s "are you still there?" warning first

const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll"] as const;

export function IdleTimeout() {
  const { user, logout } = useAuth();
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(WARN_MS / 1000));

  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tick = useRef<ReturnType<typeof setInterval> | null>(null);

  // Lets the (stable) activity listener read the current warning state without re-subscribing.
  const warningRef = useRef(false);
  warningRef.current = warning;

  const clearAll = useCallback(() => {
    if (warnTimer.current) clearTimeout(warnTimer.current);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (tick.current) clearInterval(tick.current);
    warnTimer.current = null;
    logoutTimer.current = null;
    tick.current = null;
  }, []);

  // (Re)start the countdown. Called on every burst of activity.
  const reset = useCallback(() => {
    clearAll();
    setWarning(false);

    warnTimer.current = setTimeout(() => {
      setSecondsLeft(Math.floor(WARN_MS / 1000));
      setWarning(true);

      tick.current = setInterval(() => {
        setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
      }, 1000);

      logoutTimer.current = setTimeout(() => {
        clearAll();
        logout();
      }, WARN_MS);
    }, IDLE_MS - WARN_MS);
  }, [clearAll, logout]);

  useEffect(() => {
    if (!user) return; // only arm this for a signed-in session

    reset();

    // Throttle: activity fires constantly, but we only need to know it happened recently.
    let last = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - last < 1000) return;
      last = now;
      // Don't let stray scroll/keys silently cancel the warning — the user must click "Stay".
      if (!warningRef.current) reset();
    };

    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, onActivity, { passive: true });
    return () => {
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity);
      clearAll();
    };
  }, [user, reset, clearAll]);

  if (!user || !warning) return null;

  return (
    <Modal
      open
      onClose={() => reset()}
      title="Still there?"
      footer={
        <>
          <Button variant="secondary" onClick={() => { clearAll(); logout(); }}>Sign out</Button>
          <Button onClick={() => reset()} autoFocus>Stay signed in</Button>
        </>
      }
    >
      <p className="text-sm text-gray-600">
        You&apos;ve been inactive for a while. For your security we&apos;ll sign you out in{" "}
        <strong className="tabular-nums text-gray-900">{secondsLeft}s</strong>.
      </p>
    </Modal>
  );
}
